<template>
    <div class="container mortgage-calculator" :class="calculatorName">

		<div class="row justify-content-center calculator-header">
			<CalculatorHeader>
				<h2>Stamp Duty Calculator</h2>
				<p><b>Now updated for the September 2022 “Mini Budget” update –</b> To establish how much Stamp Duty you will need to pay, enter in your purchase details below.</p>
			</CalculatorHeader>
		</div>
		<div class="row justify-content-center calculator-body">
			<div class="col-md-10 col-xl-8">
				<form class="calculator-container">

					<div class="mt-5" @keyup="resetCalculation" @change="resetCalculation">
						<InputElement
							fieldText="Property purchase price:"
						>
							<InputTypeNumber currencyPlaceholder v-model="purchasePrice"/>
						</InputElement>

						<transition name="fade-x" mode="out-in">
							<div v-show="purchasePrice">
								<BooleanChoiceButtons
									@click="resetCalculation"
									fieldText="Is everyone involved in the purchase considered to be first time buyers?"
									buttonTextLeft="Yes"
									buttonTextRight="No"								
								>
									<template v-slot:left>
										<input @click="resetCalculation" type="radio" v-model="firsttimeBuyers" value="Yes">									
									</template>
									<template v-slot:right>
										<input @click="resetCalculation" type="radio" v-model="firsttimeBuyers" value="No">											
									</template>																
								</BooleanChoiceButtons>

								<transition name="fade-x" mode="out-in">
									<div key="Yes" v-if="firsttimeBuyers === 'Yes'">
										<BooleanChoiceButtons
											@click="resetCalculation"
											fieldText="Will the property be your main residence?"
											buttonTextLeft="Yes"
											buttonTextRight="No"								
										>
											<template v-slot:left>
												<input @click="resetCalculation" type="radio" v-model="mainResidence" value="Yes">									
											</template>
											<template v-slot:right>
												<input @click="resetCalculation" type="radio" v-model="mainResidence" value="No">									
											</template>																
										</BooleanChoiceButtons>
									</div>

									<div key="No" v-if="firsttimeBuyers === 'No'">
										<BooleanChoiceButtons
											fieldText="Is this property purchase a second home, additional property, or a buy to let property?"
											buttonTextLeft="Yes"
											buttonTextRight="No"								
										>
											<template v-slot:left>
												<input @click="resetCalculation" type="radio" v-model="secondProperty" value="Yes">									
											</template>
											<template v-slot:right>
												<input @click="resetCalculation" type="radio" v-model="secondProperty" value="No">									
											</template>																
										</BooleanChoiceButtons>
									</div>
								</transition>

								<transition name="scale-in" mode="out-in">
									<div
										v-if="(purchasePrice > 0 && mainResidence) || (purchasePrice > 0 && secondProperty )"
										class="text-center mt-5 mb-3"
									>
										<div class="btn calc-btn-secondary" @click="calculate">CALCULATE</div>
									</div>
								</transition>

							</div>
						</transition>
					</div>

					<transition name="fade-y" mode="out-in">
						<div v-if="calcCompleted && purchasePrice > 0">

							<ResultsContainer class="mt-5 mb-5 calc-results-box">
								<div class="mb-3 results-title" v-if="!isFieldInvalid">Based on a purchase price of:</div>
								<span class="fcs-price" v-if="!isFieldInvalid">£{{formatNumberOutput(purchasePrice)}}</span>
								<div class="mt-3 results-title" v-if="!isFieldInvalid">Your total stamp duty liability would be:</div>
								<span class="fcs-price" v-if="!isFieldInvalid">£{{stampDutyCalculationApril}}</span>
							</ResultsContainer>				

							<SubmissionForm :calculatorName="calculatorName" :calculatorData="collateMessageBody" />

						</div>
					</transition>						

				</form>				
			</div>

		</div>

    </div>
</template>

<script>
import BooleanChoiceButtons from '@/components/utilities/booleanChoiceButtons.vue'
import CalculatorHeader from '@/components/utilities/calculatorHeader.vue'
import InputElement from '@/components/utilities/inputElement.vue'
import InputTypeNumber from '@/components/utilities/inputTypeNumber.vue'
import ResultsContainer from '@/components/utilities/resultsContainer.vue'
import SubmissionForm from '@/components/utilities/submissionForm.vue'

export default {
	components: {
		BooleanChoiceButtons,
		CalculatorHeader,
		InputElement,
		InputTypeNumber,
		ResultsContainer,
		SubmissionForm
	},	
    data() {
        return {
			calculatorName: 'stamp-duty-calculator',

			purchasePrice: 0,
			firsttimeBuyers: null,
			mainResidence: null,
			secondProperty: null,
			isFieldInvalid: false, // Validation state
			stampDutyValue: null,
            calcCompleted: false,
			dropdownOptionsMove: [
				{value: "Please select an option", text: "Please select an option"},
				{value: "march", text: "Before 31st March"},
				{value: "april", text: "After 1st April"},
			],
        }
    },
    methods: {
		validateField() {
            this.isFieldInvalid = false;
        },
        calculate() {
			this.validateField(); // Ensure the field is validated before proceeding
			this.stampDutyValue = this.stampDutyCalculationApril;
			this.calcCompleted = true;
        },
		formatNumberOutput(number) {
			const newNumber = (new Intl.NumberFormat('en-GB').format(number))
			if ( newNumber === 'NaN' ) { return 0 } return newNumber
		},		
        removeHyphensToUpperCase(str) {
            return str.replace(/(-|^)([^-]?)/g, function(_, prep, letter) { return (prep && ' ') + letter.toUpperCase(); });
        },
		standardStampDutyApril (propertyPrice, stampDuty) {
			if ( this.secondProperty == null || this.secondProperty === 'No') {
				switch (true) {
					case (propertyPrice < 125000):
						stampDuty = 0;
						break;
					case (propertyPrice >= 125000 && propertyPrice < 250000):
						stampDuty = ( propertyPrice - 125000 ) * 0.02;
						break; //2% on value over £250000
					case (propertyPrice >= 250000 && propertyPrice < 925000):
						stampDuty = ( propertyPrice - 250000 ) * 0.05 + 2500;
						break; //5% on value over £925000
					case (propertyPrice >= 925000 && propertyPrice < 1500000):
						stampDuty = ( propertyPrice - 925000 ) * 0.1 + 36250;
						break; //5% on value over £925000
					case (propertyPrice >= 1500000):
						stampDuty = ( propertyPrice - 1500000 ) * 0.12 + 93750;
					break; //12% on value over £925000							
				}
				return stampDuty;
			} else {
				switch (true) {
					case (propertyPrice < 125000):
						stampDuty = propertyPrice * 0.05;
						break; //5% on value under £250000
					case (propertyPrice >= 125000 && propertyPrice < 250000):
						stampDuty = ( propertyPrice - 125000 ) * 0.07 + 6250;
						break; //7% on value over £250000
					case (propertyPrice >= 250000 && propertyPrice < 925000):
						stampDuty = ( propertyPrice - 250000 ) * 0.1 + 15000;
						break; //5% on value over £925000
					case (propertyPrice >= 925000 && propertyPrice < 1500000):
						stampDuty = ( propertyPrice - 925000 ) * 0.15 + 88750;
						break; //5% on value over £925000
					case (propertyPrice >= 1500000):
						stampDuty = ( propertyPrice - 1500000 ) * 0.17 + 196250;
					break; //12% on value over £925000							
				}
				return stampDuty;
			}
		},
		resetCalculation() {
			this.calcCompleted = false;
		},
		formatEmptyMessageFields(message){
			return message ? message : 'N/A'
		}				
    },	
    computed: {
        stampDutyCalculationApril() {
			let stampDuty = 0
			const propertyPrice = Number(this.purchasePrice)

			if ( this.firsttimeBuyers === 'Yes' ) {
				if ( this.mainResidence === 'Yes' ) {
					switch (true) {
						case (propertyPrice < 300000):
							stampDuty = 0;
							break;
						case (propertyPrice >= 300000 && propertyPrice <= 500000):
							stampDuty = ( propertyPrice - 300000 ) * 0.05;
							break; 
						case (propertyPrice > 500000):
							stampDuty = this.standardStampDutyApril(propertyPrice, stampDuty);
					}
				} else {
					stampDuty = this.standardStampDutyApril(propertyPrice, stampDuty);
				}
			} else {
				stampDuty = this.standardStampDutyApril(propertyPrice, stampDuty);
			}
			stampDuty = this.formatNumberOutput(stampDuty);
			return stampDuty;
		},	
		collateMessageBody() {
			const calculatorData = {
				calculatorTitle: this.removeHyphensToUpperCase(this.calculatorName),
				calculatorResults: 
				`
				Property purchase price: £${parseInt(this.purchasePrice)}
				Is everyone involved in the purchase considered to be first time buyers? ${this.firsttimeBuyers}
				Will the property be your main residence? ${this.formatEmptyMessageFields(this.mainResidence)}
				Is this property purchase a second home, additional property, or a buy to let property? ${this.formatEmptyMessageFields(this.secondProperty)}
				Stamp Duty value: ${this.stampDutyValue}
				`
			}
			return calculatorData;
		}
	}
}
</script>

<style lang="scss">
	@import './styles/calcs.scss';
</style>